import { OmitStrict } from "@amx/ux-twoui-utils";
import React from "react";
import { Button, ButtonProps } from "../../../components/Buttons/Button/Button";
import { NoHrefLink, NoHrefLinkProps } from "../../../components/Links/NoHrefLink/NoHrefLink";
import { Media } from "../../../components/Media/Media";
import { Stack, StackItem } from "../../../components/Stacks/Stack/Stack";
import { TextProps } from "../../../components/Text/Text";
import { TruncatedText } from "../../../components/TruncatedText/TruncatedText";
import { validateProps } from "../../../shared/props";
import { validateChildComponent } from "../../../shared/validation";
import { boxShadowDepths } from "../../../styles";
import { makeStyles, mergeClasses } from "../../../styles/makeStyles";

/** A line item feature in a ComparisonFeature component */
export interface ComparisonFeatureLineItem {
    /**
     * The ID of an element that contains a string to serve as the label for
     * this feature
     */
    readonly "aria-labelledby": string;
    /** The React element to render for this feature */
    readonly child: React.ReactNode;
}

export interface ComparisonFeatureTextProps
    extends OmitStrict<TextProps, "block" | "variant" | "children"> {
    /** The text to render in this component */
        readonly children: string;
}

export interface ComparisonFeatureProps {
    /** The id for this comparison card */
    readonly id?: string;

    /** The string to show highlighted at the top of the card */
    readonly badge?: string;

    /** The title of the card */
    readonly title?: ComparisonFeatureTextProps;

    /** The subtitle of the card */
    readonly subtitle?: ComparisonFeatureTextProps;

    /** The description of the card */
    readonly description?: ComparisonFeatureTextProps;

    /** Actions the user can take on this card */
    readonly actions?: {
        /** The single call to action for this card */
        readonly cta: ButtonProps;

        /** The alternative action for this card */
        readonly alt?: NoHrefLinkProps;
    };

    /** The list of features to render in this card */
    readonly features: readonly ComparisonFeatureLineItem[];

    /** Style variants for this card */
    readonly variants?: {
        /** How to align the text in the card */
        readonly alignment?: "left" | "center";
    };
}

export interface ComparisonFeatureLayoutProps {
    readonly id?: string;
    readonly badge?: React.ReactNode;
    readonly title?: React.ReactNode;
    readonly subtitle?: React.ReactNode;
    readonly description?: React.ReactNode;
    readonly actions?: readonly React.ReactNode[];
    readonly features: readonly Partial<ComparisonFeatureLineItem>[];
    readonly variants?: ComparisonFeatureProps["variants"];
}

export const ComparisonFeatureCssProps = {
    columns: "--comparison-card-col",
};

const borderRadius = "4px";

const useDefaultStyles = makeStyles({
    root: (theme) => ({
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.neutralHigh,
        borderRadius: borderRadius,
        boxShadow: boxShadowDepths.hover, // To match Feature's box shadow
    }),
    badge: (theme) => ({
        // Required so TruncatedText doesn't mess with the height of the badge
        // when text is truncated
        display: "flex",
        padding: "5px 24px",
        borderTopLeftRadius: borderRadius,
        borderTopRightRadius: borderRadius,
        backgroundColor: theme.palette.highlighter,
    }),
    header: {
        margin: "10px 24px",
    },
    featureList: {
        listStyle: "none",
        margin: "0 24px",
        marginBottom: "auto",
        padding: 0,
        columns: `var(${ComparisonFeatureCssProps.columns}, auto)`,
        " > *": {
            breakInside: "avoid",
        },
    },
    actions: {
        display: "flex",
        flexDirection: "column",
        padding: "16px 24px 24px 24px",
        // Add children gap when there are multiple children
        " > *:not(:last-child)": {
            marginBottom: "12px"
        }
    },
});

const useVariantStyles = makeStyles({
    badgeCenter: {
        textAlign: "center",
        justifyContent: "center",
    },
    headerCenter: {
        textAlign: "center",
    },
    headerNoBadge: {
        marginTop: "40px",
    },
    actionsCenter: {
        textAlign: "center",
    },
});

type ComparisonFeatureLayoutFC = React.FC<ComparisonFeatureLayoutProps>;
export const ComparisonFeatureLayout: ComparisonFeatureLayoutFC = ({
    id,
    badge,
    title,
    subtitle,
    description,
    actions,
    features,
    variants,
    ...otherProps
}) => {
    // Variations
    const hasBadge = badge !== null && badge !== undefined && badge !== false;
    const isCenterVariant = variants?.alignment === "center";

    // Styling. This method of styling is inspired by FluentUI v9. See the
    // comment in makestyles.ts for more details
    const defaultClasses = useDefaultStyles();
    const variantClasses = useVariantStyles();

    const badgeClass = mergeClasses(
        defaultClasses.badge,
        isCenterVariant && variantClasses.badgeCenter
    );
    const headerClass = mergeClasses(
        defaultClasses.header,
        isCenterVariant && variantClasses.headerCenter,
        !hasBadge && variantClasses.headerNoBadge
    );
    const ctaClass = mergeClasses(
        defaultClasses.actions,
        isCenterVariant && variantClasses.actionsCenter
    );

    // The markup
    return (
        <div id={id} className={defaultClasses.root} {...otherProps}>
            {hasBadge && <div className={badgeClass}>{badge}</div>}
            {(title || subtitle || description) && (
                <div className={headerClass}>
                    {title}
                    {subtitle}
                    {description}
                </div>
            )}
            <ul className={defaultClasses.featureList}>
                {features.map((feature, i) => {
                    if (!feature.child) {
                        return null;
                    }

                    return (
                        <li
                            aria-labelledby={feature["aria-labelledby"]}
                            key={i}
                        >
                            {feature.child}
                        </li>
                    );
                })}
            </ul>
            {(actions) && <div className={ctaClass}>{actions?.map((action, i) => (<div key={i}>{action}</div>))}</div>}
        </div>
    );
};

export const ComparisonFeature: React.FC<ComparisonFeatureProps> = ({
    id,
    badge,
    title,
    subtitle,
    description,
    actions,
    features,
    variants,
    ...otherProps
}) => {
    // Validations
    for (const feature of features) {
        validateChildComponent(feature.child, [Media], "ComparisonFeature");
    }

    validateProps("ComparisonFeature", otherProps as any);

    return (
        <ComparisonFeatureLayout
            id={id}
            badge={badge && <TruncatedText>{badge}</TruncatedText>}
            title={title && <TruncatedText {...title} variant = "paneTitle" block={true} lines={2} />}
            subtitle={
                subtitle && (
                    <TruncatedText {...subtitle} variant = "bodyMediumSecondaryRegular" block={true} lines={2} />
                )
            }
            description ={
                description && (
                    <Stack padding="8px 0 0 0">
                        <StackItem>
                            <TruncatedText {...description} variant = "bodySecondaryText" block={true} lines={2} />
                        </StackItem>
                    </Stack>
                )
            }
            features={features}
            actions={actions && [
                ...(!!actions.cta ? [<Button {...actions.cta} />] : []),
                ...(!!actions.alt ? [<NoHrefLink {...actions.alt} />] : []),
            ]}
            variants={variants}
            {...otherProps}
        />
    );
};
